// NotificationPreferences.tsx
// Shown after first chart save — lets user enable "Life Timing Alerts"
// and configure which life areas generate push notifications.
// Also accessible from Settings menu for later edits.

import React, { useState, useEffect } from 'react';
import {
  Modal, ModalDialog, ModalClose, Typography, Box, Button,
  Switch, Divider, CircularProgress, Stack, Chip
} from '@mui/joy';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import { supabase } from '../utils/supabaseClient';
import { registerFCMToken } from '../utils/pushNotifications';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface NotifPrefs {
  push_enabled: boolean;
  life_timing_alerts: boolean;
  career_alerts: boolean;
  relationship_alerts: boolean;
  wealth_alerts: boolean;
  children_alerts: boolean;
  investments_alerts: boolean;
  property_alerts: boolean;
  parents_health_alerts: boolean;
  health_alerts: boolean;
  weekly_guidance: boolean;
}

const DEFAULT_PREFS: NotifPrefs = {
  push_enabled: false,
  life_timing_alerts: true,
  career_alerts: true,
  relationship_alerts: true,
  wealth_alerts: true,
  children_alerts: true,
  investments_alerts: true,
  property_alerts: true,
  parents_health_alerts: true,
  health_alerts: true,
  weekly_guidance: true,
};

interface Props {
  open: boolean;
  userId: string;
  onClose: () => void;
  /** Called after prefs saved so parent can update its state */
  onSaved?: (prefs: NotifPrefs) => void;
}

// ─── Preference rows config ───────────────────────────────────────────────────

const PREF_ROWS: Array<{
  key: keyof Omit<NotifPrefs, 'push_enabled' | 'life_timing_alerts'>;
  label: string;
  description: string;
}> = [
  { key: 'career_alerts',         label: '💼 Career',          description: 'Visibility, promotions, and professional opportunities' },
  { key: 'relationship_alerts',   label: '❤️ Relationship',    description: 'Partner connections, harmony, and important conversations' },
  { key: 'wealth_alerts',         label: '💰 Wealth',          description: 'Income, financial decisions, and investment windows' },
  { key: 'children_alerts',       label: '👶 Children',        description: 'Education, wellbeing, and key moments for your children' },
  { key: 'investments_alerts',    label: '📈 Investments',     description: 'Favourable windows for financial growth and decisions' },
  { key: 'property_alerts',       label: '🏠 Property',        description: 'Real estate and home-related timing opportunities' },
  { key: 'health_alerts',         label: '🌿 Health',          description: 'Positive periods for wellness, energy, and recovery' },
  { key: 'parents_health_alerts', label: '👴 Parents\' Health', description: 'Timing shifts relevant to your parents\' wellbeing' },
  { key: 'weekly_guidance',       label: '📅 Weekly Guidance', description: 'Your personalised weekly insight briefing every Sunday' },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function NotificationPreferences({ open, userId, onClose, onSaved }: Props) {
  const [prefs, setPrefs] = useState<NotifPrefs>(DEFAULT_PREFS);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [permissionState, setPermissionState] = useState<NotificationPermission | null>(null);

  // ── Load existing prefs when modal opens ────────────────────────────────────
  useEffect(() => {
    if (!open || !userId) return;
    setSaved(false);
    setError(null);
    setFetching(true);

    // Check current browser permission state
    if ('Notification' in window) {
      setPermissionState(Notification.permission);
    }

    supabase
      .from('notification_preferences')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle()
      .then(({ data, error: fetchErr }) => {
        setFetching(false);
        if (fetchErr) { console.warn('[NotifPrefs] fetch error', fetchErr.message); return; }
        if (data) {
          setPrefs({
            push_enabled:           data.push_enabled           ?? false,
            life_timing_alerts:     data.life_timing_alerts     ?? true,
            career_alerts:          data.career_alerts          ?? true,
            relationship_alerts:    data.relationship_alerts    ?? true,
            wealth_alerts:          data.wealth_alerts          ?? true,
            children_alerts:        data.children_alerts        ?? true,
            investments_alerts:     data.investments_alerts     ?? true,
            property_alerts:        data.property_alerts        ?? true,
            parents_health_alerts:  data.parents_health_alerts  ?? true,
            health_alerts:          data.health_alerts          ?? true,
            weekly_guidance:        data.weekly_guidance        ?? true,
          });
        }
      });
  }, [open, userId]);

  // ── Toggle a single preference key ─────────────────────────────────────────
  const toggle = (key: keyof NotifPrefs) => {
    setPrefs(prev => ({ ...prev, [key]: !prev[key] }));
    setSaved(false);
  };

  // ── Request permission + save ───────────────────────────────────────────────
  const handleEnable = async () => {
    setLoading(true);
    setError(null);

    try {
      // 1. Request browser notification permission
      let permission: NotificationPermission = 'denied';
      if ('Notification' in window) {
        permission = await Notification.requestPermission();
        setPermissionState(permission);
      }

      const pushEnabled = permission === 'granted';

      // 2. Register FCM token if permission granted — surface failures in the UI
      let regError: string | null = null;
      if (pushEnabled) {
        const reg = await registerFCMToken(userId);
        regError = reg.error;
      }

      // 3. Save preferences to DB (upsert — handles both first-time and updates)
      const toSave = {
        user_id: userId,
        push_enabled: pushEnabled,
        life_timing_alerts:     prefs.life_timing_alerts,
        career_alerts:          prefs.career_alerts,
        relationship_alerts:    prefs.relationship_alerts,
        wealth_alerts:          prefs.wealth_alerts,
        children_alerts:        prefs.children_alerts,
        investments_alerts:     prefs.investments_alerts,
        property_alerts:        prefs.property_alerts,
        parents_health_alerts:  prefs.parents_health_alerts,
        health_alerts:          prefs.health_alerts,
        weekly_guidance:        prefs.weekly_guidance,
        preferences_seen:       true,
        updated_at:             new Date().toISOString(),
      };

      const { error: upsertErr } = await supabase
        .from('notification_preferences')
        .upsert(toSave, { onConflict: 'user_id' });

      if (upsertErr) throw upsertErr;

      const savedPrefs = { ...prefs, push_enabled: pushEnabled };
      setPrefs(savedPrefs);
      onSaved?.(savedPrefs);

      if (regError) {
        // Preferences saved, but THIS device could not be registered — tell the user
        // why instead of pretending everything worked (previously silent).
        setError(`Preferences saved, but this device could not be registered for notifications: ${regError}`);
        return; // keep dialog open so the user sees the reason
      }

      setSaved(true);

      // Close after a brief confirmation pause
      setTimeout(() => onClose(), 1400);

    } catch (err: any) {
      setError(err.message ?? 'Could not save preferences');
    } finally {
      setLoading(false);
    }
  };

  // ── Save without enabling push (user just updates preferences) ─────────────
  const handleSaveOnly = async () => {
    setLoading(true);
    setError(null);
    try {
      const toSave = {
        user_id: userId,
        push_enabled:           prefs.push_enabled,
        life_timing_alerts:     prefs.life_timing_alerts,
        career_alerts:          prefs.career_alerts,
        relationship_alerts:    prefs.relationship_alerts,
        wealth_alerts:          prefs.wealth_alerts,
        children_alerts:        prefs.children_alerts,
        investments_alerts:     prefs.investments_alerts,
        property_alerts:        prefs.property_alerts,
        parents_health_alerts:  prefs.parents_health_alerts,
        health_alerts:          prefs.health_alerts,
        weekly_guidance:        prefs.weekly_guidance,
        preferences_seen:       true,
        updated_at:             new Date().toISOString(),
      };

      const { error: upsertErr } = await supabase
        .from('notification_preferences')
        .upsert(toSave, { onConflict: 'user_id' });

      if (upsertErr) throw upsertErr;

      setSaved(true);
      onSaved?.(prefs);
      setTimeout(() => onClose(), 1200);
    } catch (err: any) {
      setError(err.message ?? 'Could not save preferences');
    } finally {
      setLoading(false);
    }
  };

  const isAlreadyGranted  = permissionState === 'granted';
  const isAlreadyDenied   = permissionState === 'denied';
  const pushActive        = isAlreadyGranted && prefs.push_enabled;

  return (
    <Modal open={open} onClose={onClose} sx={{ zIndex: 10010 }}>
      <ModalDialog
        sx={{
          maxWidth: 460,
          width: '95vw',
          p: 0,
          overflow: 'hidden',
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #1a0a2e 0%, #0d1b3e 100%)',
          border: '1px solid rgba(180, 120, 255, 0.18)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
        }}
      >
        {/* Header */}
        <Box sx={{
          px: 3, pt: 3, pb: 2,
          background: 'linear-gradient(135deg, rgba(180,120,255,0.12) 0%, rgba(100,160,255,0.08) 100%)',
          borderBottom: '1px solid rgba(180,120,255,0.12)',
        }}>
          <ModalClose sx={{ color: 'rgba(255,255,255,0.5)', top: 14, right: 14 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: '10px',
              background: 'linear-gradient(135deg, #b47aff, #6495ff)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <NotificationsActiveIcon sx={{ color: '#fff', fontSize: '1.2rem' }} />
            </Box>
            <Box>
              <Typography level="title-md" sx={{ color: '#fff', fontWeight: 700 }}>
                Life Timing Alerts
              </Typography>
              {pushActive && (
                <Chip size="sm" color="success" variant="soft" sx={{ mt: 0.25 }}>
                  Active
                </Chip>
              )}
            </Box>
          </Box>

          <Typography level="body-sm" sx={{ color: 'rgba(255,255,255,0.65)', lineHeight: 1.5 }}>
            Receive personalised guidance <strong style={{ color: 'rgba(255,255,255,0.85)' }}>before</strong> important
            opportunities, life events, and timing shifts occur — based on your Vedic chart.
          </Typography>
        </Box>

        {/* Body */}
        <Box sx={{ px: 3, py: 2, maxHeight: { xs: '52vh', sm: '55vh' }, overflowY: 'auto' }}>

          {fetching ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size="sm" sx={{ color: '#b47aff' }} />
            </Box>
          ) : (
            <>
              {/* Master toggle */}
              <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                py: 1.5, mb: 0.5,
              }}>
                <Box>
                  <Typography level="title-sm" sx={{ color: '#fff', fontWeight: 600 }}>
                    ✨ Life Timing Alerts
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                    Master switch — enables all alerts below
                  </Typography>
                </Box>
                <Switch
                  checked={prefs.life_timing_alerts}
                  onChange={() => toggle('life_timing_alerts')}
                  sx={{
                    '--Switch-trackBackground': 'rgba(255,255,255,0.15)',
                    '&.Mui-checked': { '--Switch-trackBackground': '#b47aff' },
                  }}
                />
              </Box>

              <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.08)' }} />

              {/* Per-area toggles */}
              <Typography level="body-xs" sx={{ color: 'rgba(255,255,255,0.4)', mb: 1, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Notification Categories
              </Typography>

              <Stack spacing={0}>
                {PREF_ROWS.map(({ key, label, description }) => (
                  <Box
                    key={key}
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      py: 1.25,
                      borderBottom: '1px solid rgba(255,255,255,0.05)',
                      opacity: prefs.life_timing_alerts ? 1 : 0.4,
                      transition: 'opacity 0.2s',
                    }}
                  >
                    <Box sx={{ flex: 1, mr: 1 }}>
                      <Typography level="body-sm" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
                        {label}
                      </Typography>
                      <Typography level="body-xs" sx={{ color: 'rgba(255,255,255,0.45)', lineHeight: 1.3 }}>
                        {description}
                      </Typography>
                    </Box>
                    <Switch
                      checked={prefs[key] as boolean}
                      disabled={!prefs.life_timing_alerts}
                      onChange={() => toggle(key)}
                      size="sm"
                      sx={{
                        '--Switch-trackBackground': 'rgba(255,255,255,0.12)',
                        '&.Mui-checked': { '--Switch-trackBackground': '#6495ff' },
                        flexShrink: 0,
                      }}
                    />
                  </Box>
                ))}
              </Stack>

              {/* Permission denied warning */}
              {isAlreadyDenied && (
                <Box sx={{
                  mt: 2, p: 1.5, borderRadius: '8px',
                  background: 'rgba(255,100,100,0.1)',
                  border: '1px solid rgba(255,100,100,0.2)',
                }}>
                  <Typography level="body-xs" sx={{ color: 'rgba(255,180,180,0.9)' }}>
                    ⚠️ Notifications are blocked in your browser. To enable them, click the lock icon in your address bar → Notifications → Allow.
                  </Typography>
                </Box>
              )}

              {error && (
                <Typography level="body-xs" sx={{ color: '#ff8080', mt: 1 }}>
                  {error}
                </Typography>
              )}

              {saved && (
                <Typography level="body-xs" sx={{ color: '#80ffb0', mt: 1, textAlign: 'center' }}>
                  ✓ Preferences saved
                </Typography>
              )}
            </>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{
          px: 3, pb: 3, pt: 1.5,
          borderTop: '1px solid rgba(255,255,255,0.06)',
          display: 'flex', flexDirection: 'column', gap: 1,
        }}>
          {/* Primary CTA — only shown if not yet granted */}
          {!isAlreadyGranted && !isAlreadyDenied && (
            <Button
              fullWidth
              loading={loading}
              onClick={handleEnable}
              disabled={fetching}
              sx={{
                background: 'linear-gradient(135deg, #b47aff, #6495ff)',
                color: '#fff',
                fontWeight: 700,
                borderRadius: '10px',
                py: 1.25,
                '&:hover': { background: 'linear-gradient(135deg, #c48dff, #80aaff)' },
              }}
            >
              <NotificationsActiveIcon sx={{ fontSize: '1.1rem', mr: 0.75 }} />
              Enable Notifications
            </Button>
          )}

          {/* Already granted — just save preferences */}
          {(isAlreadyGranted || isAlreadyDenied) && (
            <Button
              fullWidth
              loading={loading}
              onClick={handleSaveOnly}
              disabled={fetching}
              sx={{
                background: 'linear-gradient(135deg, #b47aff, #6495ff)',
                color: '#fff',
                fontWeight: 700,
                borderRadius: '10px',
                py: 1.25,
                '&:hover': { background: 'linear-gradient(135deg, #c48dff, #80aaff)' },
              }}
            >
              Save Preferences
            </Button>
          )}

          <Button
            fullWidth
            variant="plain"
            onClick={onClose}
            sx={{ color: 'rgba(255,255,255,0.4)', py: 0.75, fontSize: '0.8rem' }}
          >
            Maybe later
          </Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
}
